import { Injectable, inject } from '@angular/core';
import { SupabaseService } from '../../../shared/services/supabase.service';
import { SegmentId } from '../segment/segment-id';
import { BrandSlug } from '../shared/brand-slug';
import type { OrgId, UserId } from '../shared/ids';
import type { MemberPreferences } from './member-preferences';
import {
  type MemberPreferencesDocument,
  parseMemberPreferencesDocument,
} from './member-preferences-document';
import { MemberPreferencesRepository } from './member-preferences-repository';

const TABLE = 'portal_org_member';
const SELECT_COLS =
  'preferences, selected_brand_slugs, selected_brands_by_category_pb, has_onboarded';

type MemberPreferencesRow = {
  preferences: unknown;
  selected_brand_slugs: unknown;
  selected_brands_by_category_pb: unknown;
  has_onboarded: boolean | null;
};

@Injectable()
export class SupabaseMemberPreferencesRepository extends MemberPreferencesRepository {
  private readonly supabase = inject(SupabaseService);

  async forMember(userId: UserId, orgId: OrgId): Promise<MemberPreferences> {
    const { data, error } = await this.supabase.client
      .from(TABLE)
      .select(SELECT_COLS)
      .eq('user_id', userId)
      .eq('org_id', orgId)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error(`portal_org_member: no row for user ${userId} in org ${orgId}`);
    return rowToMemberPreferences(userId, orgId, data as MemberPreferencesRow);
  }

  async saveDocument(
    userId: UserId,
    orgId: OrgId,
    document: MemberPreferencesDocument
  ): Promise<void> {
    await this.update(userId, orgId, {
      preferences: document as unknown as Record<string, unknown>,
    });
  }

  async saveBrandSelection(
    userId: UserId,
    orgId: OrgId,
    slugs: readonly BrandSlug[]
  ): Promise<void> {
    await this.update(userId, orgId, { selected_brand_slugs: Array.from(slugs) });
  }

  async saveCategoryComparisonBrandSelection(
    userId: UserId,
    orgId: OrgId,
    segmentId: SegmentId,
    slugs: readonly BrandSlug[]
  ): Promise<void> {
    // Read-merge-write: another tab or device may have saved a different comparison's pick
    // since this session loaded the row.
    const { data, error } = await this.supabase.client
      .from(TABLE)
      .select('selected_brands_by_category_pb')
      .eq('user_id', userId)
      .eq('org_id', orgId)
      .maybeSingle();
    if (error) throw error;
    const stored = (data as { selected_brands_by_category_pb: unknown } | null)
      ?.selected_brands_by_category_pb;
    const column: Record<string, string[]> = {};
    for (const [id, list] of toCategoryComparisonSelections(stored)) column[id] = Array.from(list);
    column[segmentId] = Array.from(slugs);
    await this.update(userId, orgId, { selected_brands_by_category_pb: column });
  }

  async markOnboarded(userId: UserId, orgId: OrgId): Promise<void> {
    await this.update(userId, orgId, { has_onboarded: true });
  }

  private async update(
    userId: UserId,
    orgId: OrgId,
    patch: Record<string, unknown>
  ): Promise<void> {
    const { error } = await this.supabase.client
      .from(TABLE)
      .update(patch)
      .eq('user_id', userId)
      .eq('org_id', orgId);
    if (error) throw error;
  }
}

function rowToMemberPreferences(
  userId: UserId,
  orgId: OrgId,
  row: MemberPreferencesRow
): MemberPreferences {
  return {
    kind: 'member-preferences',
    userId,
    orgId,
    document: parseMemberPreferencesDocument(row.preferences),
    selectedBrandSlugs: toBrandSlugs(row.selected_brand_slugs),
    selectedBrandSlugsByCategoryComparison: toCategoryComparisonSelections(
      row.selected_brands_by_category_pb
    ),
    hasOnboarded: row.has_onboarded === true,
  };
}

function toBrandSlugs(raw: unknown): readonly BrandSlug[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isNonEmptyString).map((s) => BrandSlug(s));
}

function toCategoryComparisonSelections(
  raw: unknown
): ReadonlyMap<SegmentId, readonly BrandSlug[]> {
  const map = new Map<SegmentId, readonly BrandSlug[]>();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return map;
  for (const [segmentId, slugList] of Object.entries(raw)) {
    if (!Array.isArray(slugList)) continue;
    map.set(SegmentId(segmentId), toBrandSlugs(slugList));
  }
  return map;
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}
